// src/hooks/useChatSubscription.js

import { useCallback, useRef } from 'react';
import { useChannelSubscription, useSessionSubscription } from './useAppSyncSubscription';

/**
 * Subscribe to chat events for a session.
 * Integrates with SWR: triggers revalidation on state changes,
 * appends messages directly for instant rendering.
 *
 * @param {string} sessionId
 * @param {function} mutate - SWR mutate function for chat history
 * @param {object} options - { enabled?: boolean, onSwitchSession?: (sessionID) => void }
 *
 * onSwitchSession: invoked when the agent emits a SWITCH_SESSION event (used
 * by the open_topic_in_dedicated_chat tool to navigate the user from the
 * current chat session to a different one — e.g. interview → topic chat).
 * Caller wires this to AIAssistantContext.openChatSession.
 */
export function useChatSubscription(sessionId, mutate, options = {}) {
    const { onSwitchSession } = options;
    // History reload bookkeeping. While the first load is in flight, events must not touch the cache: any
    // mutate (even one that changes nothing) makes SWR discard that load, and a delta every ~200ms then kept
    // the panel blank while firing a request per delta (review 2026-10-18). Events that arrive during a load
    // ask for ONE more reload when it finishes, so nothing committed meanwhile is missed.
    const loadingRef = useRef(false);
    const reloadAgainRef = useRef(false);
    const reload = useCallback(() => {
        if (loadingRef.current) {
            reloadAgainRef.current = true;
            return;
        }
        loadingRef.current = true;
        Promise.resolve(mutate()).catch(() => {}).finally(() => {
            loadingRef.current = false;
            if (reloadAgainRef.current) {
                reloadAgainRef.current = false;
                reload();
            }
        });
    }, [mutate]);

    const handleChatEvent = useCallback((event) => {
        const { eventType, parsedPayload } = event;

        // Apply an event to the cached history — or, when the history hasn't
        // loaded yet, fetch it instead. Never "mutate to nothing": SWR treats any
        // mutate as newer than a fetch already in flight and DISCARDS that
        // fetch's result, so an event racing the first load left the chat blank
        // until the next focus revalidation (dev 2026-09-27: every "Review with
        // Livi" chat opened on "Start a conversation" — Livi's turn starts at
        // creation, so her first events beat the panel's first fetch).
        const applyOrLoad = (updater) => {
            if (loadingRef.current) {
                reloadAgainRef.current = true;
                return;
            }
            let loaded = true;
            mutate((currentData) => {
                if (!currentData) {
                    loaded = false;
                    return currentData;
                }
                return updater(currentData);
            }, false);
            if (!loaded) reload();
        };

        switch (eventType) {
            case 'MESSAGE':
                applyOrLoad((currentData) => {
                    // Streaming handoff: when a committed assistant/system
                    // message for the turn lands, the live bubble empties — the
                    // committed message is now authoritative. The buffer keeps a
                    // seq FLOOR (the higher of what was shown and the message's
                    // metadata.stream_seq) instead of being dropped: AppSync does
                    // not guarantee delivery order, and a delta arriving after its
                    // message used to open a new bubble repeating the reply's last
                    // words. Deltas at or below the floor are ignored; later ones
                    // (the turn's next step) stream as usual. User echoes never
                    // touch it.
                    const isUser = parsedPayload.messageType === 'user';
                    const cur = currentData.streaming;
                    const sameTurn = !!cur && cur.turnId === parsedPayload.turnId;
                    const committedSeq = Number(parsedPayload.metadata?.stream_seq) || 0;
                    const setFloor = !isUser && (sameTurn || committedSeq > 0);
                    const withStreaming = (data) =>
                        setFloor
                            ? {
                                ...data,
                                streaming: {
                                    turnId: parsedPayload.turnId,
                                    seq: Math.max(sameTurn ? cur.seq || 0 : 0, committedSeq),
                                    text: '',
                                },
                            }
                            : data;

                    // Dedup by message ID
                    const existsById = currentData.messages?.some(
                        (m) => m.message_id === parsedPayload.messageId
                    );
                    if (existsById) return withStreaming(currentData);

                    // Dedup optimistic messages — match by content + message type
                    if (isUser) {
                        const existsByContent = currentData.messages?.some(
                            (m) => m.message_id?.startsWith('temp_') &&
                                m.content === parsedPayload.content
                        );
                        if (existsByContent) {
                            // Replace the optimistic message with the real one
                            return {
                                ...currentData,
                                messages: currentData.messages.map((m) =>
                                    m.message_id?.startsWith('temp_') && m.content === parsedPayload.content
                                        ? { ...m, message_id: parsedPayload.messageId, timestamp: parsedPayload.timestamp }
                                        : m
                                ),
                            };
                        }
                    }

                    const newMessage = {
                        message_id: parsedPayload.messageId,
                        message_type: parsedPayload.messageType,
                        content: parsedPayload.content,
                        user_name: parsedPayload.userName,
                        timestamp: parsedPayload.timestamp,
                        turn_id: parsedPayload.turnId,
                        tool_uses: parsedPayload.toolUses,
                        stop_reason: parsedPayload.stopReason,
                        // Hidden-message flag (metadata.bootstrap) travels on the event too, so a
                        // system-routed message never flashes into the chat before a reload.
                        metadata: parsedPayload.metadata || {},
                    };

                    return withStreaming({
                        ...currentData,
                        messages: [...(currentData.messages || []), newMessage],
                        message_count: (currentData.message_count || 0) + 1,
                    });
                });
                break;

            case 'MESSAGE_DELTA': {
                // Best-effort streaming overlay. Accumulate text deltas into a
                // single live `streaming` buffer keyed by turnId — NOT part of
                // `messages` and never persisted. The committed MESSAGE for the
                // turn replaces it (see MESSAGE handler); STATE_CHANGE clears it
                // as a backstop. Out-of-order / duplicate seq are ignored, and a
                // committed message heals any dropped delta.
                const { turnId, seq, text } = parsedPayload || {};
                applyOrLoad((currentData) => {
                    const cur = currentData.streaming;
                    if (cur && cur.turnId === turnId && cur.closed) return currentData;   // the turn ended
                    if (!cur || cur.turnId !== turnId) {
                        return {
                            ...currentData,
                            streaming: { turnId, seq: typeof seq === 'number' ? seq : 0, text: text || '' },
                        };
                    }
                    if (typeof seq === 'number' && seq <= cur.seq) return currentData;
                    return {
                        ...currentData,
                        streaming: {
                            turnId,
                            seq: typeof seq === 'number' ? seq : cur.seq,
                            text: (cur.text || '') + (text || ''),
                        },
                    };
                });
                break;
            }

            case 'STATE_CHANGE':
                // Update state in SWR cache
                applyOrLoad((currentData) => {
                    const nextState = parsedPayload.chatState;
                    // Backstop: empty any live streaming bubble when the turn
                    // ends. The committed MESSAGE normally does it first; this
                    // covers error paths. The seq floor stays so a late delta
                    // for the finished turn can't reopen the bubble.
                    const cur = currentData.streaming;
                    const clearStreaming =
                        (nextState === 'waiting_for_user' || nextState === 'error') && cur;
                    return {
                        ...currentData,
                        state: {
                            ...currentData.state,
                            state: nextState,
                        },
                        ...(clearStreaming
                            ? { streaming: { turnId: cur.turnId, seq: cur.seq || 0, text: '', closed: true } }
                            : {}),
                    };
                });
                break;

            case 'SWITCH_SESSION':
                // Agent (open_topic_in_dedicated_chat) wants the panel to
                // swap to a different chat session. Caller wires this via
                // options.onSwitchSession to AIAssistantContext.openChatSession.
                // No SWR mutation here — the new session has its own SWR
                // key and will fetch on mount.
                if (onSwitchSession && parsedPayload?.sessionID) {
                    onSwitchSession(parsedPayload.sessionID);
                }
                break;

            default:
                // Unknown event — trigger a full revalidation
                mutate();
                break;
        }
    }, [mutate, onSwitchSession, reload]);

    useChannelSubscription(sessionId, 'chat', handleChatEvent, options);
    // useSessionSubscription(sessionId, (event) => {
    //     console.log('RAW SESSION EVENT:', JSON.stringify(event));
    //     if (event.channel === 'chat') {
    //         handleChatEvent(event);
    //     }
    // }, options);
}